/**
 * Build-time feature switches.
 *
 * Device control (device console, Live Wall, Streaming page, remote input) is
 * ON by default and can be hidden with `VITE_DEVICE_CONTROL_ENABLED=false` at
 * build time. Hiding it here is cosmetic: the backend holds the real switch
 * (`DEVICE_CONTROL_ENABLED`, see backend/src/config/device-control.ts) and
 * refuses control requests while it is off, so a hidden page is never the only
 * thing standing between a user and a phone. Flip both halves together.
 *
 * Unset = enabled. It was off by default from 2026-10-12 (PR #45) until
 * 2026-10-15, when the owner asked for the Live Wall and control back.
 */

declare global {
  interface ImportMetaEnv {
    readonly VITE_DEVICE_CONTROL_ENABLED?: string;
    readonly VITE_PUBLIC_SIGNUP_ENABLED?: string;
  }
}

/** Only an explicit "false" / "0" hides device control; anything else shows it. */
export function parseDeviceControlFlag(raw: string | undefined): boolean {
  const value = (raw ?? '').trim().toLowerCase();
  return value !== 'false' && value !== '0';
}

export const DEVICE_CONTROL_ENABLED: boolean = parseDeviceControlFlag(
  import.meta.env.VITE_DEVICE_CONTROL_ENABLED,
);

/** Dashboard routes that exist only for device control. */
export const DEVICE_CONTROL_ROUTES: readonly string[] = ['/devices/live-wall', '/streaming'];

export const DEVICE_CONTROL_DISABLED_MESSAGE =
  'Device control is temporarily disabled. Scheduled posts, creators, media and the device list keep working.';

/**
 * Public self-signup ("Create Account" on the sign-in page) is OFF unless the
 * dashboard is built with `VITE_PUBLIC_SIGNUP_ENABLED=true`. New agencies join
 * through a platform admin's owner invitation. Hiding the form is cosmetic:
 * the backend refuses signup unless its own `PUBLIC_SIGNUP_ENABLED=true`
 * (backend/src/config/public-signup.ts). Flip both halves together.
 */
export function parsePublicSignupFlag(raw: string | undefined): boolean {
  const value = (raw ?? '').trim().toLowerCase();
  return value === 'true' || value === '1';
}

/** Read at render time, so tests can switch it with `vi.stubEnv`. */
export function isPublicSignupEnabled(): boolean {
  return parsePublicSignupFlag(import.meta.env.VITE_PUBLIC_SIGNUP_ENABLED);
}
